import { useCallback, useEffect, useState } from 'react';

type SetValue<T> = T | ((val: T) => T);

function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: SetValue<T>) => void] {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.log(error);
      return initialValue;
    }
  });

  useEffect(() => {
    const onLocalStorage = (event: Event) => {
      const customEvent = event as CustomEvent<{ key?: string }>;
      if (customEvent.detail?.key && customEvent.detail.key !== key) return;

      try {
        const item = window.localStorage.getItem(key);
        setStoredValue(item ? JSON.parse(item) : initialValue);
      } catch (error) {
        console.log(error);
      }
    };

    window.addEventListener('local-storage', onLocalStorage);
    return () => window.removeEventListener('local-storage', onLocalStorage);
  }, [key, initialValue]);

  const setValue = useCallback((value: SetValue<T>) => {
    try {
      setStoredValue((prev: T) => {
        const valueToStore =
          typeof value === 'function'
            ? (value as (val: T) => T)(prev)
            : value;

        window.localStorage.setItem(key, JSON.stringify(valueToStore));
        window.dispatchEvent(
          new CustomEvent('local-storage', { detail: { key } })
        );
        return valueToStore;
      });
    } catch (error) {
      console.log(error);
    }
  }, [key]);

  return [storedValue, setValue];
}

export default useLocalStorage;
